import { FilterKey } from "hooks/useOmnisearchDatagrid";

import { getCanonicalKey } from "./FilterPopover";
import { isFacilityFilterKeyType } from "./usePillNames";

export const EMPTY_KEY_ALIASES: Readonly<Record<string, string>> = {};

/**
 * The filter key an omnisearch key belongs to, undoing any alias the grid
 * applies on the way out.
 *
 * `keyAliases` maps a filter key to the key it serializes as, so a grid can
 * publish a different name in the URL than the one its column uses — the staff
 * vendor-data grid writes `org`/`vendor` for `tenantName`/`vendorName`. Reading
 * a query back means walking that the other way.
 */
function filterKeyNameFor(pairKey: string, keyAliases: Readonly<Record<string, string>>): string {
  const aliased = Object.entries(keyAliases).find(([, alias]) => alias === pairKey);
  return aliased ? aliased[0] : pairKey;
}

/**
 * The filter a pill's `key:value` term belongs to.
 *
 * Usually the canonical keys line up. Facility filters are the exception: a
 * grid commonly hangs the filter off its `facilityName` column while the picker
 * serializes to `facilityId`, and `facilityName` is not a semantic equivalent of
 * `facilityId` — so fall back to matching on the filter's type. The type check
 * matters, because on other grids `facilityName` is a plain text search.
 */
export function findFilterKeyForPair(
  pairKey: string,
  columnFilterKeys: FilterKey[],
  keyAliases: Readonly<Record<string, string>> = EMPTY_KEY_ALIASES
): FilterKey | undefined {
  // The serialized key first: a grid that both declares a filter under this
  // name and aliases something else to it means the declared one.
  const candidateKeys = [pairKey, filterKeyNameFor(pairKey, keyAliases)];
  for (const candidateKey of candidateKeys) {
    const canonicalKey = getCanonicalKey(candidateKey);
    const match = columnFilterKeys.find(
      (filterKey) => getCanonicalKey(filterKey.key) === canonicalKey
    );
    if (match) {
      return match;
    }
  }

  if (getCanonicalKey(pairKey) === getCanonicalKey("facilityId")) {
    return columnFilterKeys.find((filterKey) => isFacilityFilterKeyType(filterKey.filterKeyType));
  }

  return undefined;
}

/**
 * Filter types whose value is a list. Everything else holds a single value, so
 * a typed term replaces what is there rather than joining it.
 *
 * Mirrors how the popover renders each type: `enumAutocomplete`, the
 * `multiple*` pickers and the waste-vendor picker are multi-selects,
 * tags/state/state-of-matter are arrays, and `enum` is a plain single Select.
 */
const MULTI_VALUE_FILTER_KEY_TYPES: ReadonlySet<string> = new Set([
  "enumAutocomplete",
  "multipleFacility",
  "wasteMultipleFacility",
  "multipleTenant",
  "wasteVendor",
  "multipleUser",
  "multipleCustomerUser",
  "multipleStaffAnyUser",
  "multipleStaffUser",
  "state",
  "stateOfMatter",
  "facilityTags",
  "taskTags",
  "workflowTags",
  "wasteTags",
  "documentTags",
  "chemicalTags",
  "tags",
]);

/**
 * Whether the filter behind an omnisearch key holds more than one value.
 *
 * Keys the grid doesn't declare — page-level controls like a reporting-year
 * selector — are treated as single-valued. Typing `key:value` reads as "filter
 * by this value", so replacing is the predictable default; accumulating is the
 * special case that only makes sense for a known multi-select.
 */
export function isMultiValueKey(
  pairKey: string,
  columnFilterKeys: FilterKey[],
  keyAliases: Readonly<Record<string, string>> = EMPTY_KEY_ALIASES
): boolean {
  const filterKey = findFilterKeyForPair(pairKey, columnFilterKeys, keyAliases);
  if (!filterKey) {
    return false;
  }
  const filterKeyType = filterKey.filterKeyType ?? inferredTypeFromKey(filterKey.key);
  return MULTI_VALUE_FILTER_KEY_TYPES.has(filterKeyType);
}

/**
 * The popover infers a few types from the column name when no `filterKeyType`
 * is set; mirror the multi-valued ones so those filters behave consistently.
 */
function inferredTypeFromKey(key: string): string {
  if (key === "state") return "state";
  if (key === "tags" || key === "facilityTags") return "tags";
  if (key === "stateOfMatter") return "stateOfMatter";
  return "text";
}
